import localforage from 'localforage';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSnapshot } from 'valtio';

import { getNormalizedPathname } from '@/utils/locale';
import { isPathMatch } from '@/utils/urlUtils';

import type {
  InputMaybe,
  OrderAppAddressInput,
  OrderAppCheckoutChannelConfigQuery,
  OrderAppCheckoutLineInput,
  OrderAppDeliveryMode,
  OrderAppMetadataInput,
  Scalars,
} from '@/graphql/orderApp/api';
import type { AddressInput, CheckoutLineFragment } from '@/graphql/saleor/api';
import { useRouter } from '@/hooks/useRouter';
import {
  checkoutAddressTitleUpdate,
  checkoutBillingAddressUpdate,
  checkoutCreate,
  checkoutCustomerAttach,
  checkoutDeliveryScheduleDelete,
  checkoutDeliveryScheduleUpdate,
  checkoutEmailUpdate,
  checkoutGiftDelete,
  checkoutGiftUpdate,
  checkoutGwpUpdate,
  checkoutLineBraceletSizeUpdate,
  checkoutLinesAdd,
  checkoutLinesDelete,
  checkoutRefresh,
  checkoutRestore,
  formatAddressForCountry,
  getChannelConfig,
  isJapaneseAddress,
  orderAppCheckoutShippingAddressUpdate,
  orderAppDeliveryModeUpdate,
  setRegisterMyWatchChecked,
} from '@/lib/checkout';
import {
  CacheKey,
  GLOBAL_E_MARKET_AVAILABLE,
  NEXT_PUBLIC_IMAGES_FORMAT,
  THUMBNAIL_IMAGE_SIZE,
} from '@/lib/const';
import { EVENT_NAME } from '@/lib/gtm/const';
import { paths } from '@/lib/paths';
import { storePaymentData } from '@/lib/payment/api';
import type { PaymentMethod } from '@/lib/payment/types';
import { GLOBAL_ECART_ID_MAX_AGE, StorageHelper } from '@/lib/storage';
import type { ApiErrorWithType, Maybe } from '@/lib/types';
import { userFetch } from '@/lib/user';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { CheckoutState } from '@/states/checkout';
import { checkoutActions, checkoutState, GWP } from '@/states/checkout';
import { userState } from '@/states/user';

import { useGoogleAnalytics } from '../useGoogleAnalytics';
import { useVoucher } from '../useVoucher';

export const useCheckout = <Require extends boolean = false>() => {
  const { country, locale, productCatalogMode } = useRegionProvider();
  const { formatMessage } = useIntl();
  const { pathname } = useRouter();
  const { sendEcommerceEventByLines } = useGoogleAnalytics();
  const snapshot = useSnapshot<CheckoutState<Require>>(checkoutState);

  const isGlobalECheckoutPage = isPathMatch(
    getNormalizedPathname(pathname),
    `${paths.store['global-e'].checkout.asPath()}`,
  );

  const storageHelper = new StorageHelper();
  const localeChanged = storageHelper.getCookie('LocaleChanged');
  useEffect(() => {
    // cookie localeChanged is deleted in RegionProvider
    const checkForLocaleChange = async () => {
      if (localeChanged) {
        await refresh(true);
      }
    };

    checkForLocaleChange();
  }, [localeChanged]);
  const domain =
    typeof window !== 'undefined' ? `.${window.location.hostname}` : undefined;

  const { deleteVoucher } = useVoucher();

  const ongoingRestoreRef = useRef<Promise<any> | null>(null);

  const handleGlobalECartId = (checkoutId: string) => {
    if (
      GLOBAL_E_MARKET_AVAILABLE?.includes(`${country.slug}-${locale.slug}`) &&
      !isGlobalECheckoutPage
    ) {
      storageHelper.setCookie('GlobalECartId', checkoutId, {
        domain,
        maxAge: GLOBAL_ECART_ID_MAX_AGE,
      });
    }
  };

  const shouldReturnEarly = (checkoutStateId: string, checkoutId?: string) => {
    return !checkoutId || checkoutId === checkoutStateId;
  };

  const handleExistingCheckout = (
    checkoutStateId: string,
    checkoutId?: string,
  ) => {
    handleGlobalECartId(checkoutStateId);

    if (shouldReturnEarly(checkoutStateId, checkoutId)) {
      endProcessing({ initializing: false });
      return {
        isExpired: false,
        checkout: checkoutState.checkout,
      };
    }
    return null;
  };

  const handleCheckoutRestoration = async (checkoutId?: string) => {
    const { checkout, isChangedAfterOrderCreated, orderId, paymentMethod } =
      await checkoutRestore({
        languageCode: locale.code,
        imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
        thumbnailSize: THUMBNAIL_IMAGE_SIZE,
        id: checkoutId,
      });

    if (!checkout) {
      if (checkoutId) {
        checkoutActions.clearCheckoutLines();
      } else {
        await create();
      }
    } else {
      handleGlobalECartId(checkout.id);
      checkoutActions.updateCheckout(checkout);
    }

    if (checkoutId || checkout) {
      checkoutActions.setPaymentMethod(paymentMethod);
      checkoutActions.setOrderId(orderId);
      checkoutActions.setIsChangedAfterOrderCreated(isChangedAfterOrderCreated);
      localforage.setItem(CacheKey.CheckoutId, checkout?.id ?? checkoutId);
    }

    return checkout;
  };

  const create = async (force?: boolean) => {
    /**
     * Read the state directly to be sure if checkout was not already created.
     * Especially the case when there is an instant create & add - we cannot wait
     * for an additional rerender which would happen using `useSnapshot()`
     * https://valtio.pmnd.rs/docs/how-tos/how-to-avoid-rerenders-manually
     */
    const checkoutStateId = checkoutState.checkout?.id;

    if (checkoutStateId && !force) {
      if (
        GLOBAL_E_MARKET_AVAILABLE?.includes(`${country.slug}-${locale.slug}`)
      ) {
        const globalECartIdCookie = storageHelper.getCookie('GlobalECartId');
        if (globalECartIdCookie !== checkoutStateId && !isGlobalECheckoutPage) {
          storageHelper.setCookie('GlobalECartId', checkoutStateId, {
            domain,
            maxAge: GLOBAL_ECART_ID_MAX_AGE,
          });
        }
      }
      return;
    }
    startProcessing();

    const { checkout, errors } = await checkoutCreate({
      channelSlug: country.channelSlug,
      email: userState.user?.email,
      languageCode: locale.code,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      if (
        GLOBAL_E_MARKET_AVAILABLE?.includes(`${country.slug}-${locale.slug}`) &&
        !isGlobalECheckoutPage
      ) {
        storageHelper.setCookie('GlobalECartId', checkout.id, {
          domain,
          maxAge: GLOBAL_ECART_ID_MAX_AGE,
        });
      }

      checkoutActions.updateCheckout(checkout);
      checkoutActions.setOrderId('');
      checkoutActions.setIsChangedAfterOrderCreated(false);
      localforage.setItem(CacheKey.CheckoutId, checkout.id);
      endProcessing();
      return checkout;
    }

    endProcessing();
  };

  /**
   * Restores a checkout from storage or creates a new one if not found.
   *
   * This function implements race condition protection by ensuring only one
   * restore operation happens at a time. If multiple calls are made simultaneously,
   * all calls will wait for the first operation to complete and return the same result.
   *
   */
  const restore = async (
    checkoutId?: string,
  ): Promise<{
    checkout: any;
    isExpired: boolean;
  }> => {
    // If there's already an ongoing restore operation, wait for it to complete
    if (ongoingRestoreRef.current) {
      return await ongoingRestoreRef.current;
    }

    const restorePromise = (async () => {
      const checkoutStateId = checkoutState.checkout?.id;

      if (checkoutStateId) {
        const earlyReturn = handleExistingCheckout(checkoutStateId, checkoutId);
        if (earlyReturn) {
          return earlyReturn;
        }
      }

      checkoutActions.updateProcessingState({
        processing: true,
        initializing: true,
      });

      const checkout = await handleCheckoutRestoration(checkoutId);

      endProcessing({ initializing: false });
      return {
        isExpired: !!checkoutId && checkout === null,
        checkout: checkout,
      };
    })();

    // Store the promise reference
    ongoingRestoreRef.current = restorePromise;

    try {
      const result = await restorePromise;
      return result;
    } finally {
      // Clear the reference when the operation completes (success or error)
      ongoingRestoreRef.current = null;
    }
  };

  const lineAdd = async (
    variantId: string,
    cta: string,
    watchLineId?: InputMaybe<Scalars['ID']['input']>,
    metadata?: OrderAppMetadataInput[],
  ) => {
    metadata = metadata ?? [];
    const _lineObj = {
      quantity: 1,
      variantId,
      metadata: [
        {
          key: 'item.cta_selected',
          value: cta,
        },
        ...metadata,
      ],
    };
    if (watchLineId) {
      return await linesAdd([{ ..._lineObj, watchLineId }]);
    } else {
      return await linesAdd([_lineObj]);
    }
  };

  const linesAdd = async (lines: OrderAppCheckoutLineInput[]) => {
    if (productCatalogMode) {
      return [];
    }
    await restore();
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    startProcessing();
    await gwpProductCheck();
    const errors = await checkoutLinesAdd({
      id: checkoutStateId,
      lines,
    });

    if (errors.length) {
      checkoutActions.notify(
        errors.map((error) => ({
          type: 'orderApp',
          ...error,
          message: formatMessage({
            id: 'genericError',
          }),
        })) as ApiErrorWithType[],
      );
      endProcessing();
      return errors;
    }

    checkoutActions.setIsChangedAfterOrderCreated(true);

    // when products are added or deleted, if there is a voucher, remove it.
    if (checkoutState.checkout?.voucherCode) {
      await deleteVoucher(checkoutStateId);
    }
    const checkout = await refresh(true);
    const addedLines = lines
      .reduce((prev, { strapRelatedProducts, variantId }) => {
        return [
          ...prev,
          checkout?.lines.find(({ variant }) => variant.id === variantId),
          checkout?.lines.find(
            ({ variant }) => variant.id === strapRelatedProducts?.buckleId,
          ),
        ];
      }, [] as unknown[])
      .filter(Boolean) as CheckoutLineFragment[];

    sendEcommerceEventByLines({
      event: EVENT_NAME.ADD_TO_CART,
      lines: addedLines,
    });

    if (addedLines.length) {
      checkoutActions.notify(addedLines);
    }
    endProcessing();

    return [];
  };

  const lineDelete = async (lineId: string) => await linesDelete([lineId]);

  const linesDelete = async (lineIds: string[]) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const deletedLines = lineIds
      .reduce((prev, lineId) => {
        const line = checkoutState.checkout?.lines?.find(
          ({ id }) => id === lineId,
        ) as CheckoutLineFragment;

        if (line) {
          const relatedProductBuckle = checkoutState.checkout?.lines?.find(
            ({ variant: { id } }) => id === line.relatedProductBuckleId,
          ) as CheckoutLineFragment;

          const relatedProductStrap = checkoutState.checkout?.lines?.find(
            ({ variant: { id } }) => id === line.relatedProductStrapId,
          ) as CheckoutLineFragment;

          const relatedProductWatch = checkoutState.checkout?.lines?.find(
            ({ variant: { id } }) => id === line.relatedProductWatchId,
          ) as CheckoutLineFragment;

          return [
            ...prev,
            line,
            relatedProductWatch,
            relatedProductStrap,
            relatedProductBuckle,
          ];
        }
        return [...prev];
      }, [] as unknown[])
      .filter(Boolean) as CheckoutLineFragment[];

    startProcessing();

    await gwpProductCheck();

    const errors = await checkoutLinesDelete({
      id: checkoutStateId,
      linesIds: lineIds,
    });

    if (errors?.length) {
      checkoutActions.notify(
        errors.map((error) => ({
          type: 'orderApp',
          ...error,
          message: formatMessage({
            id: 'genericError',
          }),
        })) as ApiErrorWithType[],
      );
      endProcessing();
      return errors;
    }
    checkoutActions.setIsChangedAfterOrderCreated(true);

    // when products are added or deleted, if there is a voucher, remove it.
    if (checkoutState.checkout?.voucherCode) {
      await deleteVoucher(checkoutStateId);
    }

    if (!checkoutState.isIncludeWatch) {
      setRegisterMyWatch(false);
    }

    await refresh(true);

    sendEcommerceEventByLines({
      event: EVENT_NAME.REMOVE_FROM_CART,
      lines: deletedLines,
    });
    endProcessing();

    return [];
  };

  const gwpProductCheck = async () => {
    const gwpProduct = checkoutState?.gwpProduct;
    if (gwpProduct) {
      await gwpProductUpdate('');
    }
  };

  const gwpProductUpdate = async (gwpProductId?: string) => {
    const checkoutStateId = checkoutState.checkout?.id;
    if (!checkoutStateId) {
      return [];
    }

    const lineId = checkoutState?.checkout?.lines?.find(
      (item) =>
        item.variant.product.productType.slug.toLocaleLowerCase() === 'gift',
    )?.id;
    const errors = await checkoutGwpUpdate({
      id: checkoutStateId,
      checkoutGwp: {
        doNotNeedGift: gwpProductId === GWP.doNotNeedGift,
        gwpProductId:
          gwpProductId === GWP.doNotNeedGift ? '' : gwpProductId ?? '',
        lineIdToRemove: lineId ?? '',
      },
    });

    if (errors?.length) {
      return errors;
    }

    checkoutActions.setIsChangedAfterOrderCreated(true);

    return [];
  };

  const shippingAddressUpdate = async (id: string, address: AddressInput) => {
    if (!id) {
      return;
    }

    const errors = await orderAppCheckoutShippingAddressUpdate({
      id,
      address: formatAddressForCountry({ address }) as OrderAppAddressInput,
      ...(isJapaneseAddress(address) && {
        validationRules: { enableFieldsNormalization: false },
      }),
    });

    if (errors?.length) {
      return errors;
    }
    await refresh(true);
    checkoutActions.setIsChangedAfterOrderCreated(true);
  };

  const billingAddressUpdate = async (id: string, address: AddressInput) => {
    const { checkout, errors } = await checkoutBillingAddressUpdate({
      id,
      address: formatAddressForCountry({ address }),
      ...(isJapaneseAddress(address) && {
        validationRules: { enableFieldsNormalization: false },
      }),
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }
  };

  const addressUpdate = async (
    type: 'shipping' | 'billing',
    address: AddressInput,
  ) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return;
    }

    if (type === 'shipping') {
      return await shippingAddressUpdate(checkoutStateId, address);
    } else {
      return await billingAddressUpdate(checkoutStateId, address);
    }
  };

  const customerAttach = async (checkoutId?: string) => {
    const idToAttach = checkoutId ?? checkoutState.checkout?.id;

    const user = await userFetch();

    if (!idToAttach || idToAttach === user?.checkout?.id) {
      return;
    }

    const { checkout, errors } = await checkoutCustomerAttach({
      id: idToAttach,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }
  };

  const emailUpdate = async (email: string) => {
    const checkoutStateId = checkoutState.checkout?.id;
    const voucherCode = checkoutState.checkout?.voucherCode;

    if (!checkoutStateId) {
      return;
    }

    const { checkout, errors } = await checkoutEmailUpdate({
      id: checkoutStateId,
      email,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);

      // when email is updated, if there is a voucher and voucher is BR24, remove it.
      if (voucherCode?.startsWith('BR24')) {
        await deleteVoucher(checkoutStateId);
      }
    }
  };
  const deliveryUpdate = async (
    deliveryMode: OrderAppDeliveryMode,
    boutiqueExtReference: Maybe<string>,
    address: AddressInput,
  ) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return;
    }

    const { errors } = await orderAppDeliveryModeUpdate({
      checkoutId: checkoutStateId,
      deliveryMode,
      boutiqueExtReference,
      address: formatAddressForCountry({
        address,
        isStreetAddress2SameAsCity: true,
      }) as OrderAppAddressInput,
    });

    if (errors?.length) {
      return errors;
    }
  };

  const refresh = async (full?: boolean) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return;
    }

    const { checkout } = await checkoutRefresh({
      id: checkoutStateId,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      thumbnailSize: THUMBNAIL_IMAGE_SIZE,
      languageCode: locale.code,
      full,
    });

    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      return checkout;
    }
  };

  const giftDelete = async () => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const { checkout, errors } = await checkoutGiftDelete({
      id: checkoutStateId,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    return [];
  };

  const giftUpdate = async (giftFrom: string, giftMessage: string) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const { checkout, errors } = await checkoutGiftUpdate({
      id: checkoutStateId,
      giftFrom,
      giftMessage,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    return [];
  };

  const setRegisterMyWatch = (value: boolean) => {
    if (!checkoutState.checkout?.id) {
      return;
    }
    setRegisterMyWatchChecked(checkoutState.checkout.id, value);
  };

  const deliveryScheduleUpdate = async (timeSlot: string) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const { checkout, errors } = await checkoutDeliveryScheduleUpdate({
      id: checkoutStateId,
      timeSlot,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    return [];
  };

  const deliveryScheduleDelete = async () => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const { checkout, errors } = await checkoutDeliveryScheduleDelete({
      id: checkoutStateId,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    return [];
  };

  const braceletSizeUpdate = async (lineId: string, braceletSize: string) => {
    startProcessing();

    const { errors } = await checkoutLineBraceletSizeUpdate({
      id: lineId,
      braceletSize,
    });

    if (errors?.length) {
      endProcessing();
      return errors;
    }

    const checkout = await refresh();

    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    endProcessing();
    return [];
  };

  const storePaymentMethod = async (
    paymentMethod: Pick<PaymentMethod, 'type' | 'gateway'>,
    orderId?: string,
  ) => {
    const checkoutStateId = orderId ? orderId : checkoutState.checkout?.id;

    if (checkoutStateId) {
      await storePaymentData({
        paymentMethod: JSON.stringify(paymentMethod),
        id: checkoutStateId,
      });
    }
  };
  const addressTitleUpdate = async (
    shippingTitle: string,
    billingTitle: string,
  ) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const { checkout, errors } = await checkoutAddressTitleUpdate({
      id: checkoutStateId,
      shippingTitle,
      billingTitle,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    return [];
  };

  const deliveryMethodsUpdate = async () => {
    const checkoutStateId = checkoutState.checkout?.id;
    const checkoutDeliveryModes = checkoutState.deliveryMethods;

    if (!checkoutStateId) {
      return;
    }

    const channelConfig: OrderAppCheckoutChannelConfigQuery['channelConfig'] =
      await getChannelConfig({
        channel: country.channelSlug,
      });

    if (
      channelConfig &&
      !arraysAreEqual(channelConfig.deliveryModes, checkoutDeliveryModes)
    ) {
      checkoutActions.updateDeliveryMethods(channelConfig.deliveryModes);
    }
  };

  const startProcessing = (
    opts?: Parameters<typeof checkoutActions.updateProcessingState>[number],
  ) =>
    checkoutActions.updateProcessingState({
      processing: true,
      ...opts,
    });

  const endProcessing = (
    opts?: Parameters<typeof checkoutActions.updateProcessingState>[number],
  ) =>
    checkoutActions.updateProcessingState({
      processing: false,
      clearErrors: false,
      ...opts,
    });

  return {
    addressUpdate,
    deliveryUpdate,
    deliveryScheduleDelete,
    deliveryScheduleUpdate,
    emailUpdate,
    customerAttach,
    lineAdd,
    lineDelete,
    linesAdd,
    linesDelete,
    create,
    restore,
    giftDelete,
    giftUpdate,
    braceletSizeUpdate,
    addressTitleUpdate,
    refresh,
    startProcessing,
    endProcessing,
    gwpProductUpdate,
    snapshot,
    storePaymentMethod,
    deliveryMethodsUpdate,
    isGlobalECheckoutPage,
  };
};

export type UseCheckout = ReturnType<typeof useCheckout>;

export const arraysAreEqual = (array1: string[], array2: string[]) => {
  // If the lengths of the arrays are different, return false
  if (array1.length !== array2.length) {
    return false;
  }

  return isEqual(sortBy(array1), sortBy(array2));
};
